import { useState } from 'react';
import axios from 'axios';
import { getToken } from '../Services/BearerToken';

/**
 * Custom hook for CSV export functionality
 * @param {string} endpoint - API endpoint to call (e.g., '/bookings')
 * @param {object} filters - Current filters applied to the data
 * @returns {object} - exportCsv function and isExporting state
 */
export default function useCsvExport(endpoint, filters) {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);

  const exportCsv = async () => {
    setIsExporting(true);
    setError(null);

    try {
      const token = getToken();
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');

      // Prepare params with export=csv as query parameter and filters
      const params = {
        filters,
        export: 'csv',
      };

      // Make request with responseType blob to handle binary data
      const response = await axios.get(
        process.env.REACT_APP_API_URL + endpoint,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        },
      );

      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = `export-${timestamp}.csv`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          [, filename] = filenameMatch;
        }
      }

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to export CSV');
      // eslint-disable-next-line no-console
      console.error('CSV export error:', err);
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportCsv,
    isExporting,
    error,
  };
}
